import { useState, useEffect, useCallback } from 'react';
import {
  useRatingsStore,
  type Rating,
  type FlaggedCategory,
} from '../stores/ratingsStore';
import './RatingsPage.css';

const RATING_OPTIONS: Rating[] = ['Yes', 'No', 'Consider'];

export default function RatingsPage() {
  const {
    ratings,
    summary,
    categories,
    models,
    flagged,
    reevalThreshold,
    loading,
    error,
    fetchRatings,
    setRating,
  } = useRatingsStore();

  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    fetchRatings();
  }, [fetchRatings]);

  const handleSetRating = useCallback(
    async (category: string, model: string, rating: Rating | null, notes?: string): Promise<void> => {
      try {
        await setRating(category, model, rating, notes);
      } catch (err) {
        setLocalError((err as Error).message);
      }
    },
    [setRating]
  );

  const shownError = error || localError;

  return (
    <div className="ratings-page">
      <div className="ratings-header">
        <h1 className="ratings-title">Model Ratings</h1>
        <p className="ratings-subtitle">
          Rate each model per content category. When more than {reevalThreshold} models
          earn a "Yes" in a category, it's flagged for a head-to-head re-evaluation.
        </p>
      </div>

      {shownError && (
        <div className="ratings-error">
          <span>{shownError}</span>
          <button
            onClick={() => {
              setLocalError(null);
              useRatingsStore.setState({ error: null });
            }}
          >
            Dismiss
          </button>
        </div>
      )}

      {flagged.length > 0 && (
        <ReevalPanel flagged={flagged} threshold={reevalThreshold} />
      )}

      {loading && <p className="ratings-loading">Loading ratings...</p>}

      {!loading && categories.length === 0 && (
        <p className="ratings-empty">No content categories to rate yet.</p>
      )}

      {!loading && categories.length > 0 && (
        <div className="ratings-grid-wrap">
          <table className="ratings-grid">
            <thead>
              <tr>
                <th className="grid-corner">Category</th>
                {models.map((m) => (
                  <th key={m.id} className="grid-model-head">
                    <span className="grid-model-name">{m.displayName}</span>
                    <span className="grid-model-meta">{m.provider}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {categories.map((category) => {
                const flaggedCat = summary[category]?.needsReeval;
                const yesCount = summary[category]?.yes?.length ?? 0;
                return (
                  <tr
                    key={category}
                    className={`grid-row ${flaggedCat ? 'row-flagged' : ''}`}
                  >
                    <th className="grid-row-head">
                      <span className="grid-category-name">{formatCategory(category)}</span>
                      {flaggedCat && (
                        <span className="grid-flag-badge">
                          {yesCount} Yes — re-eval
                        </span>
                      )}
                    </th>
                    {models.map((m) => (
                      <td key={m.id} className="grid-cell">
                        <RatingCellControl
                          value={ratings[category]?.[m.id]?.rating ?? null}
                          notes={ratings[category]?.[m.id]?.notes ?? ''}
                          onRate={(rating) => handleSetRating(category, m.id, rating)}
                          onNotes={(notes) =>
                            handleSetRating(
                              category,
                              m.id,
                              ratings[category]?.[m.id]?.rating ?? null,
                              notes
                            )
                          }
                        />
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

interface ReevalPanelProps {
  flagged: FlaggedCategory[];
  threshold: number;
}

function ReevalPanel({ flagged, threshold }: ReevalPanelProps) {
  return (
    <div className="reeval-panel">
      <div className="reeval-panel-head">
        <span className="reeval-panel-title">Re-evaluation needed</span>
        <span className="reeval-panel-note">
          These categories have more than {threshold} acceptable models — too many to
          keep. Compare them head-to-head and narrow the field.
        </span>
      </div>
      <div className="reeval-list">
        {flagged.map((f) => (
          <div key={f.category} className="reeval-item">
            <div className="reeval-item-head">
              <span className="reeval-category">{formatCategory(f.category)}</span>
              <span className="reeval-count">{f.yesCount} Yes</span>
            </div>
            <div className="reeval-models">
              {f.yesModels.map((m) => (
                <span key={m} className="reeval-model-chip">{m}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

interface RatingCellControlProps {
  value: Rating | null;
  notes: string;
  onRate: (rating: Rating | null) => void;
  onNotes: (notes: string) => void;
}

function RatingCellControl({ value, notes, onRate, onNotes }: RatingCellControlProps) {
  const [showNotes, setShowNotes] = useState<boolean>(false);
  const [draft, setDraft] = useState<string>(notes);

  useEffect(() => {
    setDraft(notes);
  }, [notes]);

  const commitNotes = (): void => {
    if (draft !== notes) onNotes(draft);
    setShowNotes(false);
  };

  return (
    <div className="rating-cell">
      <div className="rating-segmented" role="group">
        {RATING_OPTIONS.map((opt) => (
          <button
            key={opt}
            type="button"
            className={`seg-btn seg-${opt.toLowerCase()} ${value === opt ? 'seg-active' : ''}`}
            onClick={() => onRate(value === opt ? null : opt)}
          >
            {opt === 'Consider' ? 'Maybe' : opt}
          </button>
        ))}
      </div>
      <button
        type="button"
        className={`rating-notes-toggle ${notes ? 'has-notes' : ''}`}
        onClick={() => setShowNotes((s) => !s)}
        title={notes || 'Add note'}
      >
        {notes ? 'Note ●' : 'Note'}
      </button>
      {showNotes && (
        <div className="rating-notes-editor">
          <textarea
            className="rating-notes-input"
            value={draft}
            placeholder="Why this rating?"
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitNotes}
            autoFocus
          />
        </div>
      )}
    </div>
  );
}

function formatCategory(category: string): string {
  return category.replace(/[_-]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
